import React, { useEffect, useState, useRef } from "react";
import { Avatar, Dropdown as Notify, Spin, Divider } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import Bell from "../../assets/icons/bell.svg";
import newBell from "../../assets/icons/newbell.svg";
import Purple from "../../assets/icons/purpleCircle.svg";
import { useSelector } from "react-redux";
import {
  getNotifications,
  updateNotification,
  markAllAsRead,
} from "../../api/notifications";
import profilePic from "./../../assets/icons/profilepic.png";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import useOutsideClick from "../../share/hooks/useClickOutSide";

const AppNotifications = () => {
  const [notificationData, setNotificationData] = useState({
    pageNumber: 1,
    itemPerPage: 8,
    items: [],
    totalPages: 0,
  });
  const [width, setWidth] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [isReadMoreLoading, setIsReadMoreLoading] = useState(false);

  // Ref
  const containerRef = useRef(null);
  const readMoreBtnRef = useRef(null);

  // Custom hook to handle clicks outside the notification list, excluding the "Read More" button
  useOutsideClick(
    containerRef,
    () => {
      setNotificationData(() => ({
        items: [],
        pageNumber: 1,
        totalPages: 0,
        itemPerPage: 8,
      }));
      setDropdownVisible(false);
    },
    [readMoreBtnRef]
  );

  // Router
  const navigate = useNavigate();

  const notificationState = useSelector((state) => state?.user);
  function timeAgo(timestamp) {
    const currentDate = moment();
    const providedDate = moment(timestamp);
    const duration = moment.duration(currentDate.diff(providedDate));

    if (duration.asDays() > 0) {
      return moment(timestamp).fromNow();
    } else if (duration.asHours() > 0) {
      return moment(timestamp).fromNow();
    } else {
      return moment(timestamp).fromNow();
    }
  }

  const fetchNotifications = async (pageNumber, itemPerPage) => {
    setIsLoading(true);
    const res = await getNotifications({ pageNumber, itemPerPage });

    if (res) {
      setNotificationData(() => ({
        ...notificationData,
        items: res?.items,
        pageNumber: +res?.currentPage,
        totalPages: +res?.totalPages,
      }));
      setIsLoading(false);
    }
  };

  const handleUpdateNotification = async (id) =>
    await updateNotification(id, { isRead: true });

  useEffect(() => {
    const handleResize = () => {
      setWidth(window?.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleReadAll = async () =>
    await markAllAsRead({ isCustomerNotification: false });

  const handleNavigate = async (notification) => {
    const updatedNotification = await handleUpdateNotification(
      notification?._id
    );
    if (updatedNotification) {
      if (updatedNotification?.type === "order") navigate("/main/orders");
      else if (notification?.type === "advertisement-approve")
        navigate("/main/ads/pending-ads");
      else if (notification?.type === "advertisement-reject")
        navigate("main/ads/pending-ads");
      else if (notification?.type === "appointment")
        navigate("/main/orders/appointments");
    }
  };

  const handleReadMore = async () => {
    setIsReadMoreLoading(true);
    const res = await getNotifications({
      pageNumber: notificationData?.pageNumber + 1,
      itemPerPage: notificationData?.itemPerPage,
    });
    if (res) {
      setNotificationData(() => ({
        ...notificationData,
        items: [...notificationData?.items, ...res?.items],
        pageNumber: +res?.currentPage,
        totalPages: +res?.totalPages,
      }));
      setIsReadMoreLoading(false);
    }
  };

  const notifications = [
    {
      label: (
        <div>
          <div className="flex justify-between px-2 hover:bg-white bg-white">
            <p className="font-medium">Notifications</p>
            <p
              className="text-primaryPurple place-items-end font-medium ml-4 hover:text-mediumPurple"
              onClick={handleReadAll}
            >
              Mark all as read
            </p>
          </div>
        </div>
      ),
      key: "0",
    },
    {
      type: "divider",
    },
    ...(notificationData?.items?.length > 0
      ? notificationData?.items?.map((notification, i) => ({
          label: (
            <div className="w-40">
              <div
                className={`${
                  !notification?.isRead && "bg-lightGrey"
                } flex items-center h-8 px-2`}
                key={i}
                onClick={() => handleNavigate(notification)}
              >
                <div className="flex justify-between items-center content-end">
                  {notification?.user?.profileImage ? (
                    <Avatar
                      src={notification?.user?.profileImage}
                      className=" h-4 mt-0 w-4 mr-2"
                    />
                  ) : (
                    <Avatar src={profilePic} className=" h-4 mt-0 w-4 mr-2" />
                  )}
                  <div>
                    <span className="font-medium">
                      <> {notification?.title && <>{notification.title}{" "}</>}</>
                    </span>

                    <span className="font-normal text-xs">
                      {notification?.body}
                      <br />
                    </span>

                    <div>
                      <span className="font-normal text-xs">
                        {timeAgo(notification?.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Divider className=" h-1px m-0" plain />
              </div>
            </div>
          ),
          key: `${notification?._id}`, // Use a unique key, here I used i + 1
        }))
      : [{ label: <div>No Notifications</div>, key: "no-notifications" }]),
    {
      label: isReadMoreLoading && (
        <div className="flex justify-center px-2 hover:bg-white bg-white pt-1">
          <Spin indicator={<LoadingOutlined />} size="medium" />
        </div>
      ),
      key: "1001234",
    },
    {
      label: (
        <div
          className="flex justify-center px-2 hover:bg-white bg-white"
          ref={readMoreBtnRef}
        >
          {notificationData?.totalPages > notificationData?.pageNumber && (
            <p
              className="text-primaryPurple place-items-end font-medium hover:text-mediumPurple m-0"
              ref={readMoreBtnRef}
              onClick={handleReadMore}
            >
              {!isReadMoreLoading && "view more"}
            </p>
          )}
        </div>
      ),
      key: "1044440",
    },
  ];

  const laodingNotifications = [
    {
      label: (
        <div className="md:w-40 w-40 flex h-50 justify-center items-center hover:bg-white bg-white">
          <Spin indicator={<LoadingOutlined />} size="large" />
        </div>
      ),
      key: "15",
    },
  ];

  return (
    <>
      <div className="" ref={containerRef}>
        <Notify
          trigger={["click"]}
          menu={{ items: isLoading ? laodingNotifications : notifications }}
          open={dropdownVisible}
          // onOpenChange={handleDropdownVisibleChange}
          placement={width && width <= 768 ? "bottom" : "bottomRight"}
          overlayStyle={{
            overflow: "scroll",
            maxHeight: "450px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          {notificationState?.notification?.isNew === false ? (
            <img
              src={dropdownVisible ? Purple : Bell}
              className="h-5 w-5 hover:cursor-pointer ml-5"
              onClick={() => {
                setDropdownVisible(() => !dropdownVisible);
                fetchNotifications(
                  notificationData?.pageNumber,
                  notificationData?.itemPerPage
                );
              }}
            />
          ) : (
            <img
              src={dropdownVisible ? Purple : newBell}
              className="h-5 w-5 hover:cursor-pointer"
              onClick={() => {
                setDropdownVisible(() => !dropdownVisible);
                fetchNotifications(
                  notificationData?.pageNumber,
                  notificationData?.itemPerPage
                );
              }}
            />
          )}
        </Notify>
      </div>
    </>
  );
};

export default AppNotifications;
